import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, Lock, Browser, DeviceMobile, Megaphone, Image as ImageIcon, FilmSlate, SquaresFour, ArrowRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getTemplatesApi } from "@/lib/api";

const ICONS = { website: Browser, app: DeviceMobile, marketing: Megaphone, image: ImageIcon, video: FilmSlate };

function TemplateCard({ t, toolLabel, onPick }) {
  const Icon = ICONS[t.category] || SquaresFour;
  return (
    <button data-testid={`template-card-${t.id}`} onClick={() => onPick(t)} className="group relative flex flex-col items-start rounded-2xl border border-black/5 bg-white p-4 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-14px_rgba(0,0,0,0.25)]">
      <div className="flex w-full items-center justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-soft text-brand"><Icon size={18} weight="duotone" /></span>
        {t.locked
          ? <span data-testid={`template-lock-${t.id}`} className="flex items-center gap-1 rounded-full bg-ink px-2 py-0.5 text-[10px] font-semibold text-white"><Lock size={10} weight="bold" /> Pro</span>
          : <span className="text-[11px] font-medium text-mist">{toolLabel}</span>}
      </div>
      <p className="mt-3 text-sm font-semibold text-ink">{t.title}</p>
      <p className="mt-1 text-xs leading-relaxed text-mist">{t.description}</p>
      <span className="mt-3 flex items-center gap-1 text-xs font-semibold text-brand opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
        {t.locked ? "Unlock with Pro" : "Use template"} <ArrowRight size={12} weight="bold" />
      </span>
    </button>
  );
}

export function TemplatesGallery({ open, onOpenChange, tools = [], onUse }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ categories: [], items: [], pro: false });
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) getTemplatesApi().then(setData).catch(() => toast.error("Could not load templates"));
  }, [open]);

  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    return data.items.filter((t) => (cat === "all" || t.category === cat) && (!s || `${t.title} ${t.description} ${t.category} ${t.target_tool}`.toLowerCase().includes(s)));
  }, [data, cat, q]);

  const label = (id) => tools.find((x) => x.id === id)?.label || id;
  const pick = (t) => {
    if (t.locked) {
      toast("This template is part of Pro", { description: "Upgrade to unlock all 30 starter templates." });
      onOpenChange(false);
      return navigate("/pricing");
    }
    onUse(t);
    onOpenChange(false);
  };

  const tabs = [{ id: "all", label: "All" }, ...data.categories];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="templates-dialog" onOpenAutoFocus={(e) => window.matchMedia("(max-width: 639px)").matches && e.preventDefault()} className="bottom-0 top-auto max-h-[92dvh] max-w-none translate-y-0 grid-cols-[minmax(0,1fr)] overflow-hidden rounded-t-3xl border-0 bg-[#FBFBFD] p-0 pb-[env(safe-area-inset-bottom)] data-[state=closed]:slide-out-to-bottom-[100%] data-[state=open]:slide-in-from-bottom-[100%] sm:bottom-auto sm:top-[50%] sm:max-h-[88vh] sm:max-w-3xl sm:translate-y-[-50%] sm:rounded-3xl sm:data-[state=open]:slide-in-from-top-[48%]">
        <div className="flex max-h-[92dvh] min-w-0 flex-col sm:max-h-[88vh]">
          <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-black/15 sm:hidden" aria-hidden />
          <DialogHeader className="px-5 pt-4 text-left sm:px-6 sm:pt-6">
            <DialogTitle className="font-display text-2xl font-semibold text-ink">Start from a template</DialogTitle>
            <DialogDescription className="text-sm text-mist">Pick one, fill in the [brackets], then create your prompt.{!data.pro && " Pro unlocks every template."}</DialogDescription>
          </DialogHeader>
          <div className="min-w-0 px-5 pt-4 sm:px-6">
            <div className="flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-4 focus-within:border-brand focus-within:ring-4 focus-within:ring-brand/15">
              <MagnifyingGlass size={16} className="text-mist" />
              <input data-testid="templates-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates" className="h-full flex-1 bg-transparent text-sm text-ink outline-none" />
            </div>
            <div className="stp-noscroll -mx-5 mt-3 flex gap-1.5 overflow-x-auto px-5 pb-1 sm:mx-0 sm:px-0" data-testid="templates-tabs">
              {tabs.map((c) => (
                <button key={c.id} data-testid={`templates-tab-${c.id}`} onClick={() => setCat(c.id)} className={`min-h-[36px] whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${cat === c.id ? "bg-ink text-white" : "bg-black/5 text-ink hover:bg-black/10"}`}>{c.label}</button>
              ))}
            </div>
          </div>
          <div className="grid flex-1 grid-cols-1 gap-3 overflow-y-auto overscroll-contain px-5 pb-6 pt-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-3" data-testid="templates-grid">
            {items.map((t) => <TemplateCard key={t.id} t={t} toolLabel={label(t.target_tool)} onPick={pick} />)}
            {!items.length && <p data-testid="templates-empty" className="col-span-full py-10 text-center text-sm text-mist">No templates match “{q}”.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
